import React, {useState, useEffect, useRef, useMemo} from "react";
import axios from "axios";
import {Button} from "primereact/button";
import {FloatLabel} from "primereact/floatlabel";
import {InputText} from "primereact/inputtext";
import {FileUpload} from "primereact/fileupload";
import {ProgressBar} from "primereact/progressbar";
import {reportingVariables} from "./components/static/reporting_variables_static";
import {bootstrapColors, primeIcons} from "../static/colors_icons_ui";
import IconSelector from "../processes_documentation/widgets/IconSelector";
import ColorSelector from "../processes_documentation/widgets/ColorSelector";
import VariableSelector from "../processes_documentation/widgets/VariableSelector";
import CreatingCard from "../processes_documentation/widgets/CreatingCard";
import DisabledFormFields from "../processes_documentation/widgets/DisabledFormFields";
import {Tooltip} from "primereact/tooltip";
import {FaFile} from "react-icons/fa";
import {Chip} from "primereact/chip";
import {Calendar} from "primereact/calendar";
import {AutoComplete} from "primereact/autocomplete";
import {contextTypes} from "./components/static/contect_types";

import {base, reports_card} from "../../api";
import {t} from "i18next";

export default function ReportingProcessesEditForm({
	reporting,
	setReporting,
	fetchReportings,
	editDialogVisible,
	setEditDialogVisible,
	onSave,
	toast,
}) {
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [formFields, setFormFields] = useState([]);
	const availableReportingVars = reportingVariables.filter(
		reportingVariable => !formFields.some(field => field.type === reportingVariable.type),
	);

	console.log({availableReportingVars, formFields, reportingVariables});
	const availableKeys = contextTypes.filter(
		contextType => !selectedKeys.some(key => key.type === contextType.type),
	);

	const [formName, setFormName] = useState("");
	const [slug, setSlug] = useState("");
	const [selectedColor, setSelectedColor] = useState(bootstrapColors[0].color);
	const [selectedIcon, setSelectedIcon] = useState(primeIcons[0]);
	const fileUploadRef = useRef(null);
	const [totalSize, setTotalSize] = useState(0);

	useEffect(() => {
		const reportSelectedContexts = reporting.selected_contexts;
		const reportSelectedKeys = contextTypes.filter(contextType =>
			reportSelectedContexts.some(selectedContext => selectedContext === contextType.type),
		);
		if (reporting) {
			setFormName(reporting.name || "");
			setSlug(reporting.slug || "");
			setSelectedColor(reporting.color || bootstrapColors[0].color);
			setSelectedIcon(reporting.icon || primeIcons[0]);
			setSelectedKeys(reportSelectedKeys);
			let loadedVars = [];
			try {
				loadedVars = JSON.parse(reporting.form_fields || "[]");
			} catch (e) {
				loadedVars = [];
			}
			setFormFields(loadedVars);
		}
	}, [reporting]);

	const handleCancel = () => {
		setEditDialogVisible(false);
		toast.current.show({severity: "error", summary: "Closed", detail: "Edit canceled", life: 3000});
	};
	const handleChipClick = key => {
		setSelectedKeys([...selectedKeys, key]);
	};

	const handleRemoveInput = key => {
		setSelectedKeys(selectedKeys.filter(item => item.type !== key.type));
	};

	const handleSave = async () => {
		const formData = new FormData();
		formData.append("name", formName);
		formData.append("slug", slug);
		formData.append("color", selectedColor);
		formData.append("icon", selectedIcon);
		formData.append("form_fields", JSON.stringify(formFields));
		formData.append("selected_contexts", JSON.stringify(selectedKeys.map(item => item.type)));

		const files = fileUploadRef.current?.getFiles() || [];
		files.forEach(file => formData.append("uploaded_files", file));

		try {
			await axios.put(`${base + reports_card}/${reporting.id}/`, formData, {
				headers: {"Content-Type": "multipart/form-data"},
			});
			fetchReportings();
			onSave();
			setEditDialogVisible(false);
			toast.current.show({
				severity: "success",
				summary: "Saved",
				detail: "Process updated",
				life: 3000,
			});
		} catch (error) {
			toast.current.show({
				severity: "error",
				summary: "Error",
				detail: "Update failed",
				life: 3000,
			});
		}
	};

	const onTemplateSelect = e => {
		let _totalSize = totalSize;
		let files = e.files;

		Object.keys(files).forEach(key => {
			_totalSize += files[key].size || 0;
		});

		setTotalSize(_totalSize);
	};

	const onTemplateUpload = e => {
		let _totalSize = 0;

		e.files.forEach(file => {
			_totalSize += file.size || 0;
		});

		setTotalSize(_totalSize);
		toast.current.show({severity: "info", summary: "Success", detail: "File Uploaded"});
	};

	const onTemplateRemove = (file, callback) => {
		setTotalSize(totalSize - file.size);
		callback();
	};

	const onTemplateClear = () => {
		setTotalSize(0);
	};

	const headerTemplate = options => {
		const {className, chooseButton} = options;
		const formattedSize = fileUploadRef.current
			? fileUploadRef.current.formatSize(totalSize)
			: "0 B";

		return (
			<div className={className} style={{display: "flex", alignItems: "center"}}>
				{chooseButton}
				<div className="ml-auto d-flex align-items-center gap-3">
					<span>{formattedSize} / 10 MB</span>
					<ProgressBar value={totalSize / 10000} showValue={false} style={{width: "10rem"}} />
				</div>
			</div>
		);
	};

	const itemTemplate = (file, props) => {
		return (
			<div className="w-100 d-flex justify-content-between align-items-center py-0">
				<span className="text-start text-primary">
					{file.name}
					<small className="text-info ms-1">({props.formatSize})</small>
				</span>
				<Button
					type="button"
					icon="pi pi-times"
					text
					rounded
					severity="danger"
					onClick={() => onTemplateRemove(file, props.onRemove)}
				/>
			</div>
		);
	};

	const emptyTemplate = () => {
		return (
			<div className="flex align-items-center flex-column">
				<i
					className="pi pi-image mt-2 p-3"
					style={{
						fontSize: "1.5em",
						borderRadius: "50%",
						backgroundColor: "var(--surface-b)",
						color: "var(--surface-d)",
					}}></i>
				<span style={{fontSize: "1.2em", color: "var(--text-color-secondary)"}} className="mt-2">
					Drag and Drop Image Here
				</span>
			</div>
		);
	};
	const chooseOptions = {
		icon: "pi pi-arrow-up",
		iconOnly: true,
		className: "custom-choose-btn p-button-rounded p-button-outlined",
	};
	useEffect(() => {
		const dialogContent = document.querySelector(".p-dialog .p-dialog-content");
		if (dialogContent) dialogContent.scrollTop = 0;
	}, [editDialogVisible]);

	return (
		<div className="col-12">
			<div className="row">
				<div className="col-xl-3 col-lg-4 col-md-4 col-12 stretch-card p-3">
					<CreatingCard
						selectedIcon={selectedIcon}
						formName={formName}
						selectedColor={selectedColor}
					/>
				</div>
				<div className="col-xl-9 col-lg-8 col-md-8 col-12 stretch-card p-3">
					<div className="row">
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								value={formName}
								onChange={e => {
									setFormName(e.target.value);
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									);
								}}
							/>
							<label className="ps-3 pt-0">{t("Card Title")}</label>
						</FloatLabel>
						<FloatLabel className="col-6">
							<InputText
								className="w-100"
								value={slug}
								onChange={e =>
									setSlug(
										e.target.value
											.toLowerCase()
											.replace(/\s+/g, "-")
											.replace(/[^a-z0-9_-]/g, ""),
									)
								}
							/>
							<label className="ps-3 pt-0">{t("Slug/url")}</label>
						</FloatLabel>

						<IconSelector
							selectedIcon={selectedIcon}
							setSelectedIcon={setSelectedIcon}
							selectedColor={selectedColor}
						/>
						<ColorSelector selectedColor={selectedColor} setSelectedColor={setSelectedColor} />
					</div>
				</div>
				<div className="row">
					<div className="col-12 fs-5 fw-bold opacity-50 border-bottom pb-2 mb-2 p-0">
						Form example
					</div>
					<div className="col-12">
						<div className="d-flex flex-wrap gap-2 mb-3">
							{availableKeys.map(item => (
								<Button
									key={item.name}
									label={item.name}
									icon="pi pi-plus"
									className="m-1 "
									rounded
									outlined
									severity="secondary"
									onClick={() => handleChipClick(item)}
								/>
							))}
						</div>
						<hr />

						{selectedKeys.map(item => (
							<Button
								key={item.key}
								label={item.key || item.name}
								icon="pi pi-times"
								className="m-1 "
								rounded
								outlined
								severity="secondary"
								onClick={() => handleRemoveInput(item)}
							/>
						))}

						{selectedKeys.length === 0 && (
							<>
								<div className="d-flex justify-content-center align-items-center p-5 opacity-50">
									Selected variables will be shown here
								</div>
							</>
						)}

						<div className="col-12 fs-5 fw-bold opacity-50 border-bottom mt-5 mb-3 p-0">
							Variables
						</div>
						<div className="col-12">
							<div className="d-flex flex-wrap gap-2 mb-3">
								{availableReportingVars.map(item => (
									<Button
										key={item.variable}
										label={item.description}
										icon="pi pi-plus"
										className="m-1"
										rounded
										outlined
										severity="secondary"
										onClick={() => setFormFields(prev => [...prev, item])}
									/>
								))}
							</div>
							{availableReportingVars.length > 0 && <hr />}
							<div className="col-12 mb-3">
								<div className="row">
									{formFields.map(item => (
										<div className="col-sm-6 col-12 mb-3" key={item.variable}>
											<label className="d-block fw-semibold">{item.description}</label>
											<small className="text-danger d-block mb-1 opacity-75">
												{item.type} - {item.field}
											</small>
											{item.field === "calendar" ? (
												<div className="d-flex align-items-center justify-content-center">
													<Calendar className="w-100" showIcon disabled />
													<Button
														rounded
														icon="pi pi-trash"
														className="border-0 ms-2"
														outlined
														severity="danger"
														onClick={() =>
															setFormFields(prev => prev.filter(v => v.type !== item.type))
														}
													/>
												</div>
											) : item.field === "autocomplate" ? (
												<div className="d-flex align-items-center justify-content-center">
													<AutoComplete className="w-100" dropdown disabled />
													<Button
														rounded
														icon="pi pi-trash"
														className="border-0 ms-2"
														outlined
														severity="danger"
														onClick={() =>
															setFormFields(prev => prev.filter(v => v.type !== item.type))
														}
													/>
												</div>
											) : null}
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div>
					<div className="col-12 pb-3">
						{reporting?.file_templates?.length > 0 && (
							<>
								<div className="fs-3 fw-bold opacity-75 border-bottom pb-3 mb-3">Files</div>
								<div className="row">
									{reporting.file_templates.map(file => {
										const fileName = file.file_name.split("/").pop();
										const prefixMatch = fileName.match(/^[^_]+_/);
										const cleanedFileName = prefixMatch
											? fileName.replace(prefixMatch[0], "")
											: fileName;
										return (
											<div className="col-4 d-flex align-items-center my-2" key={file.id}>
												<FaFile className="me-2 text-primary" />
												<a
													href={file.file}
													className="text-primary w-auto text-truncate text-capitalize">
													{cleanedFileName
														.replace(/^\d+/, "")
														.replace(/_/g, " ")
														.replace(/\.[^/.]+$/, "")
														.toLowerCase()
														.replace(/\s+/g, " ")}
												</a>
											</div>
										);
									})}
								</div>
							</>
						)}
					</div>

					<Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
					<Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />
					<Tooltip target=".custom-cancel-btn" content="Clear" position="bottom" />

					<FileUpload
						ref={fileUploadRef}
						name="demo[]"
						url="/api/upload"
						multiple
						accept=".doc, .docx, .xls, .xlsx"
						maxFileSize={1000000}
						onUpload={onTemplateUpload}
						onSelect={onTemplateSelect}
						onError={onTemplateClear}
						onClear={onTemplateClear}
						headerTemplate={headerTemplate}
						itemTemplate={itemTemplate}
						emptyTemplate={emptyTemplate}
						chooseOptions={chooseOptions}
					/>
				</div>
			</div>

			<div className="col-12 mt-5 text-center border-top pt-3">
				<Button
					label={t("Cancel")}
					icon="pi pi-times"
					onClick={handleCancel}
					className="mx-3"
					rounded
					text
				/>
				<Button
					label={t("Save")}
					icon="pi pi-check"
					onClick={handleSave}
					className="mx-3"
					rounded
				/>
			</div>
		</div>
	);
}
